import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { BarraDeLista, BuscaDaBarra, FiltrosAtivos, RodapeDaLista, usePaginacao } from '../../BarraDeLista';
import { useCaminho } from '../../Caminho';
import { data, nomeDoContato, telefoneDigitadoLegivel, telefoneLegivel } from '../../formato';
import { useSessao } from '../../sessao';
import { degrauDe, ESTAGIO_DA_OPORTUNIDADE, SITUACAO_DO_ATENDIMENTO } from '../../situacoes';
import { CamposDaFicha, TabelaComFicha, type ColunaComFicha, type FichaDaLinha } from '../../TabelaComFicha';
import { Carregando, ErroDaApi, RespostaDaAcao, Selo, Vazio } from '../../ui';
import { api } from '../../api';
import { LINHA, PAPEL, localDaObra, textoDe } from './crm';
import { JanelaDoContato, GRAVAR_CONTATO, type ContatoDaJanela } from './JanelaDoContato';
import { JanelaNovaConversa } from './JanelaNovaConversa';
import { JanelaLigarCliente } from './JanelasDaOportunidade';
import './oportunidades.css';

/**
 * Os contatos do Comercial — onda 550 (Plano 2 · Comercial, Etapa 1, Tarefa
 * 15; spec do Comercial §5.3 item 4). `/comercial/contatos` e
 * `/comercial/contato/:id`.
 *
 * Quem escreveu, ligou ou foi cadastrado, com os clientes de que é contato e
 * o papel em cada um (o projetista de uma construtora, o consultor de outra),
 * as oportunidades e as conversas. Segue a carteira (`fn_contato_visivel`):
 * o lead que só escreveu no número do engenheiro não aparece para os outros.
 * O contato do cadastro de clientes muda em Clientes e obras.
 *
 * Onda 554 (Parte B, Tarefa 7; spec §3.3 e §3.5, quadro 3 do canvas): o
 * «Novo contato» no cabeçalho; quem chegou sozinho (não gravado) aparece com o
 * número, o perfil do WhatsApp em cinza («~Carla M.») e o selo «Não gravado»,
 * com «Gravar contato» na linha; os outros, com «Chamar no WhatsApp», que abre
 * a Nova conversa já com o contato. Os botões são de quem responde
 * (`atendimento.responder`); no celular, moram na ficha.
 */

interface ItemDoContato {
  id: string; nome: string; cargo: string | null; email: string | null; telefone: string | null;
  whatsapp: string | null; origem: string; naoReceberEm: string | null;
  salvo: boolean; whatsappPerfil: string | null;
  clientes: { id: string; nome: string; papel: string }[];
}

interface DetalheDoContato {
  contato: Omit<ItemDoContato, 'clientes'> & { podeMudar: boolean };
  clientes: { id: string; nome: string; razaoSocial: string; estrategico: boolean; papel: string }[];
  oportunidades: { id: string; estagio: string; linha: string | null; local: { municipio: string | null; uf: string | null };
    criadaEm: string; responsavel: string | null }[];
  conversas: { id: string; situacao: string; abertaEm: string; canal: string }[];
}

const clientesDe = (c: ItemDoContato) =>
  c.clientes.map((x) => `${x.nome} (${textoDe(PAPEL, x.papel).toLowerCase()})`).join(', ');

/** O nome do contato na lista; o de quem chegou sozinho, com o perfil do WhatsApp e o selo. */
function NomeNaLista({ c }: { c: ItemDoContato }) {
  return (
    <>
      <Link to={`/comercial/contato/${c.id}`}>{nomeDoContato(c.nome)}</Link>
      {!c.salvo && c.whatsappPerfil && <span className="contato-perfil"> ~{c.whatsappPerfil}</span>}
      {!c.salvo && <> <Selo tom="atencao">Não gravado</Selo></>}
      {c.cargo && <span className="sub">{c.cargo}</span>}
    </>
  );
}

/** A ação da linha: «Gravar contato» para quem chegou sozinho; «Chamar no WhatsApp» para os outros. */
function AcaoDoContato({ c, aoSalvar, aoChamar }: { c: ItemDoContato; aoSalvar(): void; aoChamar(): void }) {
  if (!c.salvo) {
    return <button type="button" className="botao secundario" onClick={aoSalvar}>{GRAVAR_CONTATO}</button>;
  }
  if (c.naoReceberEm) {
    return (
      <span className="contato-nao-chamar">
        <button type="button" className="botao secundario" aria-disabled="true">Chamar no WhatsApp</button>
        <span className="sub">Pediu para não receber mensagens em {data(c.naoReceberEm)}.</span>
      </span>
    );
  }
  return <button type="button" className="botao secundario" onClick={aoChamar}>Chamar no WhatsApp</button>;
}

const COLUNAS_FIXAS: ColunaComFicha<ItemDoContato>[] = [
  {
    chave: 'nome',
    cabeca: <th scope="col">Contato</th>,
    celula: (c) => <th scope="row"><NomeNaLista c={c} /></th>,
  },
  {
    chave: 'clientes',
    cabeca: <th scope="col">Clientes</th>,
    celula: (c) => <td>{c.clientes.length ? clientesDe(c) : <span className="nada">ainda sem cliente</span>}</td>,
  },
  {
    chave: 'whatsapp', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">WhatsApp</th>,
    celula: (c) => <td className="col-opcional">{c.whatsapp ? telefoneLegivel(c.whatsapp) : <span className="nada">sem WhatsApp</span>}</td>,
  },
  {
    chave: 'email', detalhe: true,
    cabeca: <th scope="col" className="col-opcional">E-mail</th>,
    celula: (c) => <td className="col-opcional">{c.email ?? <span className="nada">sem e-mail</span>}</td>,
  },
];

function fichaDoContato(c: ItemDoContato, acao: ReactNode): FichaDaLinha {
  return {
    rotulo: 'Contato',
    titulo: nomeDoContato(c.nome),
    sub: c.cargo ?? undefined,
    corpo: (
      <CamposDaFicha itens={[
        ['Clientes', c.clientes.length ? clientesDe(c) : 'ainda sem cliente'],
        ['WhatsApp', c.whatsapp ? telefoneLegivel(c.whatsapp) : 'sem WhatsApp'],
        ['Telefone', c.telefone ? telefoneDigitadoLegivel(c.telefone) : c.telefone],
        ['E-mail', c.email ?? 'sem e-mail'],
        ['De onde', c.origem === 'cadastro' ? 'Cadastro de clientes' : 'Comercial (chegou pela caixa ou foi criado à mão)'],
      ]} />
    ),
    acoes: <>{acao}<Link className="botao secundario" to={`/comercial/contato/${c.id}`}>Abrir o contato</Link></>,
  };
}

export function Contatos() {
  const { pode } = useSessao();
  const navegar = useNavigate();
  const responde = pode('atendimento.responder');
  const [janela, setJanela] = useState<null | { modo: 'novo' } | { modo: 'salvar'; c: ContatoDaJanela }>(null);
  const [chamar, setChamar] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [texto, setTexto] = useState('');
  const [lista, setLista] = useState<ItemDoContato[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);

  const buscar = useCallback(async () => {
    const q = new URLSearchParams();
    if (texto.trim()) q.set('busca', texto.trim());
    try {
      setLista(await api<ItemDoContato[]>(`/comercial/contatos?${q}`));
      setErro(null);
    } catch (e) { setErro(e); }
  }, [texto]);

  useEffect(() => {
    const t = window.setTimeout(() => { void buscar(); }, 250);
    return () => window.clearTimeout(t);
  }, [buscar]);
  const pag = usePaginacao(lista ?? [], 50, texto);

  const acaoDe = useCallback((c: ItemDoContato) => (responde ? (
    <AcaoDoContato c={c} aoSalvar={() => { setOk(null); setJanela({ modo: 'salvar', c }); }}
      aoChamar={() => { setOk(null); setChamar(c.id); }} />
  ) : null), [responde]);
  const colunas = useMemo<ColunaComFicha<ItemDoContato>[]>(() => (responde ? [
    ...COLUNAS_FIXAS,
    { chave: 'acao', cabeca: <th scope="col"><span className="so-leitor">Ação</span></th>,
      celula: (c) => <td className="contato-acao">{acaoDe(c)}</td> },
  ] : COLUNAS_FIXAS), [responde, acaoDe]);

  return (
    <div className="op-lista-tela">
      <header className="cabeca cabeca-lista">
        <div className="cresce">
          <h1>Contatos</h1>
          <p className="sub">Quem fala com o Comercial, e de quais clientes é contato.</p>
        </div>
        {responde && (
          <button type="button" className="botao" onClick={() => { setOk(null); setJanela({ modo: 'novo' }); }}>
            Novo contato
          </button>
        )}
      </header>
      <RespostaDaAcao erro={null} ok={ok} />
      <BarraDeLista rotulo="Filtros dos contatos">
        <BuscaDaBarra valor={texto} aoMudar={setTexto} placeholder="Nome, e-mail, cargo ou WhatsApp"
          rotulo="Buscar contato" />
      </BarraDeLista>
      <FiltrosAtivos itens={texto.trim() ? [{ chave: 'busca', texto: `«${texto.trim()}»`, tirar: () => setTexto('') }] : []}
        aoLimpar={() => setTexto('')}
        contagem={lista ? `${lista.length} ${lista.length === 1 ? 'contato' : 'contatos'}` : undefined} />
      {erro && !lista ? <ErroDaApi erro={erro} />
        : !lista ? <Carregando texto="Carregando os contatos" />
          : lista.length === 0 ? (
            <Vazio>{texto.trim() ? 'Nenhum contato com essa busca.' : 'Nenhum contato que você veja ainda.'}</Vazio>
          ) : (
            <div className="cartao op-lista">
              <TabelaComFicha linhas={pag.visiveis} chave={(c) => c.id} nome={(c) => nomeDoContato(c.nome)} rotulo="Contatos"
                colunas={colunas} ficha={(c) => fichaDoContato(c, acaoDe(c))} destino={(c) => `/comercial/contato/${c.id}`}
                cartao={(c) => (
                  <span className="op-cartao-celular">
                    <strong>{nomeDoContato(c.nome)}</strong>
                    <span className="sub">{c.clientes.length ? clientesDe(c) : 'ainda sem cliente'}</span>
                  </span>
                )} />
              <RodapeDaLista {...pag} teto={200} />
            </div>
          )}

      {janela && (
        <JanelaDoContato modo={janela.modo} contato={janela.modo === 'salvar' ? janela.c : null} aoFechar={() => setJanela(null)}
          aoGravar={() => {
            setOk('Contato gravado.');
            setJanela(null);
            void buscar();
          }} />
      )}
      <JanelaNovaConversa aberta={chamar !== null} inicial={chamar ? { contatoId: chamar } : undefined}
        aoFechar={() => setChamar(null)}
        aoAbrir={(conversaId, rascunho) => {
          setChamar(null);
          navegar(`/comercial/conversa/${conversaId}`, rascunho ? { state: { rascunho } } : undefined);
        }} />
    </div>
  );
}

export function ContatoFicha() {
  const { id = '' } = useParams<{ id: string }>();
  const { pode } = useSessao();
  const [d, setD] = useState<DetalheDoContato | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [janela, setJanela] = useState<null | 'mudar' | 'cliente'>(null);
  useCaminho(d ? [{ rotulo: nomeDoContato(d.contato.nome) }] : null);

  const buscar = useCallback(async () => {
    try {
      setD(await api<DetalheDoContato>(`/comercial/contatos/${id}`));
      setErro(null);
    } catch (e) { setErro(e); }
  }, [id]);
  useEffect(() => { setD(null); void buscar(); }, [buscar]);

  if (erro && !d) return <ErroDaApi erro={erro} />;
  if (!d) return <Carregando texto="Carregando o contato" />;
  const c = d.contato;
  const responde = pode('atendimento.responder');
  const feito = (t: string) => { setJanela(null); setOk(t); void buscar(); };

  return (
    <div className="op-tela">
      <header className="cabeca op-cabeca">
        <div className="cresce">
          <h1>{nomeDoContato(c.nome)}</h1>
          <p className="sub">{[c.cargo, c.origem === 'cadastro' ? 'do cadastro de clientes' : 'do Comercial'].filter(Boolean).join(' · ')}</p>
        </div>
        {responde && (
          <div className="op-acoes">
            {c.podeMudar && <button type="button" className="botao secundario" onClick={() => setJanela('mudar')}>Mudar</button>}
            <button type="button" className="botao" onClick={() => setJanela('cliente')}>Ligar a um cliente</button>
          </div>
        )}
      </header>
      <RespostaDaAcao erro={null} ok={ok} />
      {c.naoReceberEm && (
        <p className="op-quem-cuida">
          Pediu para não receber mensagens em {data(c.naoReceberEm)}: o agente, o acompanhamento e a indicação não
          falam mais com ele.
        </p>
      )}

      <div className="op-grade">
        <div className="op-coluna">
          <section className="cartao op-cartao">
            <div className="cartao-cabeca"><h2>Clientes</h2></div>
            {d.clientes.length === 0 ? (
              <p className="op-vazio">Ainda sem cliente: é um pedido novo. Quando ele fechar, o cliente entra aqui.</p>
            ) : (
              <ul className="contato-clientes">
                {d.clientes.map((x) => (
                  <li key={x.id}>
                    <span className="op-tarefa-titulo">{x.nome}</span>
                    <span className="op-aux">{textoDe(PAPEL, x.papel)}</span>
                    {x.estrategico && <Selo tom="andamento">Estratégico</Selo>}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <section className="cartao op-cartao">
            <div className="cartao-cabeca"><h2>Oportunidades</h2></div>
            {d.oportunidades.length === 0 ? <p className="op-vazio">Nenhuma oportunidade que você veja.</p> : (
              <ul className="op-conversas">
                {d.oportunidades.map((o) => {
                  const e = degrauDe(ESTAGIO_DA_OPORTUNIDADE, o.estagio);
                  return (
                    <li key={o.id}>
                      <Link to={`/comercial/oportunidade/${o.id}`}>
                        {[o.linha ? textoDe(LINHA, o.linha) : 'Linha a identificar', localDaObra(o.local)].filter(Boolean).join(' · ')}
                      </Link>
                      <span className="op-aux">desde {data(o.criadaEm)} · {o.responsavel ?? 'ninguém ainda'}</span>
                      <Selo tom={e.tom}>{e.texto}</Selo>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
        <div className="op-coluna">
          <section className="cartao op-cartao">
            <div className="cartao-cabeca"><h2>Como falar com ele</h2></div>
            <dl className="op-pares">
              <div className="op-par"><dt>WhatsApp</dt><dd>{c.whatsapp ? telefoneLegivel(c.whatsapp) : 'sem WhatsApp'}</dd></div>
              <div className="op-par"><dt>Telefone</dt><dd>{telefoneDigitadoLegivel(c.telefone) || 'sem telefone'}</dd></div>
              <div className="op-par"><dt>E-mail</dt><dd>{c.email || 'sem e-mail'}</dd></div>
            </dl>
            {!c.podeMudar && (
              <p className="op-vazio">É do cadastro de clientes: quem o muda é quem cuida de Clientes e obras.</p>
            )}
          </section>
          <section className="cartao op-cartao">
            <div className="cartao-cabeca"><h2>Conversas</h2></div>
            {d.conversas.length === 0 ? <p className="op-vazio">Nenhuma conversa que você veja.</p> : (
              <ul className="op-conversas">
                {d.conversas.map((x) => {
                  const s = degrauDe(SITUACAO_DO_ATENDIMENTO, x.situacao);
                  return (
                    <li key={x.id}>
                      <Link to={`/comercial/conversa/${x.id}`}>{x.canal}</Link>
                      <span className="op-aux">aberta em {data(x.abertaEm)}</span>
                      <Selo tom={s.tom}>{s.texto}</Selo>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
      </div>

      <JanelaDoContato modo="mudar" aberta={janela === 'mudar'} contato={c} aoFechar={() => setJanela(null)}
        aoGravar={() => feito('Contato gravado.')} />
      <JanelaLigarCliente aberta={janela === 'cliente'} contato={{ id: c.id, nome: c.nome }} aoFechar={() => setJanela(null)}
        aoGravar={() => feito('Cliente ligado ao contato.')} />
    </div>
  );
}
